import {Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import NumericInput from 'ui-library/inputs/NumericInput';
import {NumericInputChangeEvent} from 'util/Types';
import BaseValidationModal from 'validation_rules/components/simple_ui/BaseValidationRuleModal';

import {SimpleRuleInput} from './types';
import useSimpleRules, {
  ValidationRuleConstantInput,
  SimpleRulesState,
  LegacySimpleRules,
} from './useSimpleRules';
import {doesSimpleRuleExist, getInputForSimpleRule} from './utils';

export interface BiointelligentLinkFieldModalProps {
  schemaField: SchemaField;
  onRequestSubmit: (result: SimpleRulesState) => void;
  onRequestClose: () => void;
  enableMultiSelectOption?: boolean;
}

type NumericSimpleInput = string | number | null;

const NUMBER_LONGEST_COMPONENTS_INPUT_KEY = 'number_longest_components';
const MAX_LENGTH_INPUT_KEY = 'max_length';
const MIN_LENGTH_INPUT_KEY = 'min_length';

interface FieldErrors {
  numLongestComponentsError: string | null;
  maxLengthError: string | null;
  minLengthError: string | null;
  rangeError: string | null;
}

interface BaseBiointelligentLinkFieldModalProps extends BiointelligentLinkFieldModalProps {
  componentName: string;
  residueName: string;
  numLongestComponentsTemplateId: string;
  maxLengthTemplateId: string;
  minLengthTemplateId: string;
  disableOverlappingTemplateId?: string;
}

export default function BaseBiointelligentLinkFieldModal({
  schemaField,
  componentName,
  residueName,
  numLongestComponentsTemplateId,
  maxLengthTemplateId,
  minLengthTemplateId,
  disableOverlappingTemplateId,
  onRequestSubmit,
  onRequestClose,
  enableMultiSelectOption,
}: BaseBiointelligentLinkFieldModalProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(schemaField);

  const {simpleRules, legacySimpleRules} = simpleRulesState;

  const numLongestComponentsInput = getInputForSimpleRule(
    simpleRules,
    numLongestComponentsTemplateId,
    NUMBER_LONGEST_COMPONENTS_INPUT_KEY
  ) as NumericSimpleInput;
  const maxLengthInput = getInputForSimpleRule(
    simpleRules,
    maxLengthTemplateId,
    MAX_LENGTH_INPUT_KEY
  ) as NumericSimpleInput;
  const minLengthInput = getInputForSimpleRule(
    simpleRules,
    minLengthTemplateId,
    MIN_LENGTH_INPUT_KEY
  ) as NumericSimpleInput;
  const disableOverlappingInput = disableOverlappingTemplateId
    ? doesSimpleRuleExist(simpleRules, disableOverlappingTemplateId)
    : false;

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
    numLongestComponentsError: null,
    minLengthError: null,
    maxLengthError: null,
    rangeError: null,
  });

  const hasErrors = Boolean(
    fieldErrors.minLengthError ||
    fieldErrors.maxLengthError ||
    fieldErrors.numLongestComponentsError ||
    fieldErrors.rangeError
  );

  function onChangeInputValue(
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput | null
  ): void {
    // Remove the rule if the input is empty
    if (constantInputs == null) {
      simpleRulesActions.handleRemoveSimpleRule(validationRuleTemplateId);
    } else {
      simpleRulesActions.handleUpdateSimpleRule(validationRuleTemplateId, constantInputs);
    }
  }

  function validateMinMaxLengths(minValue: NumericSimpleInput, maxValue: NumericSimpleInput): string | null {
    // Validate min and max length relationship
    if (minValue != null && maxValue != null) {
      const minNum = Number(minValue);
      const maxNum = Number(maxValue);
      if (minNum > maxNum) {
        return 'Minimum length cannot be greater than maximum length';
      }
    }

    return null;
  }

  function getConstantInput(
    inputKey: string,
    input: SimpleRuleInput | null
  ): ValidationRuleConstantInput | null {
    if (input == null || input === '') {
      return null;
    }
    return {[inputKey]: input};
  }

  /** Handle updates to rules and additional interactions with values outside the base validation rules. */
  function handleUpdateLegacySimpleRulesAndEffects(legacySimpleRules: LegacySimpleRules): void {
    simpleRulesActions.handleUpdateLegacySimpleRules(legacySimpleRules);
    // Reset number of longest components if multi-select is turned off
    if (!legacySimpleRules.isMulti) {
      onChangeInputValue(numLongestComponentsTemplateId, null);
    }
  }

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={<FieldDefinitionLabel field={schemaField} schemaFieldSelectors={[]} />}
      size={ChipSize.MINI}
    />
  );

  return (
    <BaseValidationModal
      title={
        BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3 ? 'Autofill and validation rules' : 'Autofill rules'
      }
      onRequestSubmit={() => {
        onRequestSubmit(simpleRulesState);
      }}
      onRequestClose={onRequestClose}
      disabled={hasErrors}
      fieldDefinitionLabel={fieldDefinitionLabel}
      hideRestrictValuesHeader={true}
      legacySimpleRules={legacySimpleRules}
      handleUpdateLegacyRules={handleUpdateLegacySimpleRulesAndEffects}
      enableMultiSelectOption={enableMultiSelectOption}
    >
      {!BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3 && (
        <div className={css(styles.description)}>
          Data submitted to this {fieldDefinitionLabel} field must follow these rules.
        </div>
      )}
      {disableOverlappingTemplateId && (
        <div className={classNames(css(styles.disableOverlappingSection), css(styles.ruleSectionBorder))}>
          <div>
            <div className={css(styles.ruleTitle)}>{`Disable overlapping ${componentName}`}</div>
            <div className={css(styles.ruleSubtitle)}>
              {`Overlapping ${componentName} will`}
              <b>{disableOverlappingInput ? ' not ' : ' '}</b>
              be autofilled.
            </div>
          </div>
          <span
            className={css(styles.switch)}
            {...getTestProps({component: 'Switch', key: 'disableOverlapping'})}
            data-amp-safe={false}
          >
            <Switch
              checked={disableOverlappingInput}
              onChange={(e) => {
                onChangeInputValue(disableOverlappingTemplateId, e.currentTarget.checked ? {} : null);
              }}
              size="md"
            />
          </span>
        </div>
      )}
      <div className={classNames(css(styles.ruleSection), css(styles.ruleSectionBorder))}>
        <div>
          <div className={css(styles.ruleTitle)}>{`Only fill longest ${componentName}`}</div>
          <div className={css(styles.ruleSubtitle)}>
            {`Only the longest ${componentName} up to a defined number will be autofilled.`}
          </div>
        </div>
        <div>
          <InputWrapper
            id="numLongestComponents"
            label={`Number of ${componentName}`}
            labelStyle={LABEL_STYLES.LABEL}
            error={
              legacySimpleRules.isMulti
                ? fieldErrors.numLongestComponentsError != null
                  ? {message: fieldErrors.numLongestComponentsError, style: InputErrorMessageStyle.ERROR}
                  : null
                : {
                    message: `Turn on multi-select to set the maximum number of ${componentName}`,
                    style: InputErrorMessageStyle.INFO,
                  }
            }
          >
            <NumericInput
              validateInteger={true}
              min={1}
              value={legacySimpleRules.isMulti ? numLongestComponentsInput : null}
              onChange={(e: NumericInputChangeEvent) => {
                if (e.target.isValid) {
                  onChangeInputValue(
                    numLongestComponentsTemplateId,
                    getConstantInput(NUMBER_LONGEST_COMPONENTS_INPUT_KEY, e.target.numericValue)
                  );
                  setFieldErrors({...fieldErrors, numLongestComponentsError: null});
                } else {
                  setFieldErrors({...fieldErrors, numLongestComponentsError: e.target.errorMessage!});
                }
              }}
              dataTestAttrs={{component: 'NumericInput', key: 'numLongestComponents'}}
              isDisabled={!legacySimpleRules.isMulti}
            />
          </InputWrapper>
        </div>
      </div>
      <div className={css(styles.ruleSection)}>
        <div>
          <div className={css(styles.ruleTitle)}>{`Limit ${residueName} lengths`}</div>
          <div className={css(styles.ruleSubtitle)}>
            {`Only ${componentName} with lengths within the specified ${residueName} length range will be autofilled. Limits will also be enforced by registry validation.`}
          </div>
        </div>
        <InputWrapper
          className={css(styles.residueLengthInputContainer)}
          id="lengthRange"
          error={{
            message: fieldErrors.rangeError,
            style: InputErrorMessageStyle.ERROR,
          }}
        >
          <div className={css(styles.residueLengthInputSection)}>
            <InputWrapper
              className={css(styles.residueLengthInput)}
              id="minLength"
              label={`Minimum ${residueName} length`}
              labelStyle={LABEL_STYLES.LABEL}
              error={
                fieldErrors.minLengthError != null
                  ? {message: fieldErrors.minLengthError, style: InputErrorMessageStyle.ERROR}
                  : null
              }
            >
              <NumericInput
                min={1}
                validateInteger={true}
                value={minLengthInput}
                onChange={(e: NumericInputChangeEvent) => {
                  if (e.target.isValid) {
                    const newMinValue = e.target.numericValue;
                    onChangeInputValue(
                      minLengthTemplateId,
                      getConstantInput(MIN_LENGTH_INPUT_KEY, newMinValue)
                    );
                    // Revalidate both min and max when min changes
                    const validationError = validateMinMaxLengths(newMinValue, maxLengthInput);
                    setFieldErrors({
                      ...fieldErrors,
                      minLengthError: null,
                      rangeError: validationError,
                    });
                  } else {
                    setFieldErrors({...fieldErrors, minLengthError: e.target.errorMessage!});
                  }
                }}
                dataTestAttrs={{component: 'NumericInput', key: 'minLength'}}
              />
            </InputWrapper>
            <InputWrapper
              className={css(styles.residueLengthInput)}
              id="maxLength"
              label={`Maximum ${residueName} length`}
              labelStyle={LABEL_STYLES.LABEL}
              error={
                fieldErrors.maxLengthError != null
                  ? {message: fieldErrors.maxLengthError, style: InputErrorMessageStyle.ERROR}
                  : null
              }
            >
              <NumericInput
                validateInteger={true}
                value={maxLengthInput}
                onChange={(e: NumericInputChangeEvent) => {
                  if (e.target.isValid) {
                    const newMaxValue = e.target.numericValue;
                    onChangeInputValue(
                      maxLengthTemplateId,
                      getConstantInput(MAX_LENGTH_INPUT_KEY, newMaxValue)
                    );
                    // Revalidate both min and max when max changes
                    const validationError = validateMinMaxLengths(minLengthInput, newMaxValue);
                    setFieldErrors({
                      ...fieldErrors,
                      maxLengthError: null,
                      rangeError: validationError,
                    });
                  } else {
                    setFieldErrors({...fieldErrors, maxLengthError: e.target.errorMessage!});
                  }
                }}
                dataTestAttrs={{component: 'NumericInput', key: 'maxLength'}}
              />
            </InputWrapper>
          </div>
        </InputWrapper>
      </div>
    </BaseValidationModal>
  );
}

export const styles = StyleSheet.create({
  fieldDefinitionLabel: {
    marginRight: 0,
  },
  description: {
    marginBottom: '16px',
  },
  ruleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
  },
  ruleSectionBorder: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    paddingBottom: '16px',
    marginBottom: '16px',
  },
  ruleTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    lineHeight: '16px',
  },
  ruleSubtitle: {
    fontSize: '12px',
    color: 'grey',
    lineHeight: '16px',
  },
  residueLengthInputSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    width: '100%',
  },
  residueLengthInputContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  residueLengthInput: {
    width: '100%',
  },
  switch: {
    float: 'right',
  },
  disableOverlappingSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
